import type { DBEquipmentCategory, DBEquipmentItem } from "@/lib/db-types";
import type { EquipmentStatus } from "./constants";

/** Skjemaet i «Registrer utstyr» / «Rediger» (mockup C). Alle felt som tekst. */
export interface EquipmentFormValues {
  name: string;
  category_id: string;
  department_id: string;
  quantity: string;
  serial_numbers: string;
  status: EquipmentStatus;
  insured_until: string;
  insurance_value: string;
  purchase_price: string;
  purchase_date: string;
  rental_price_day: string;
  parts: string;
  comment: string;
  bookable: boolean;
}

export function toFormValues(
  item: DBEquipmentItem | null,
  categories: DBEquipmentCategory[],
  defaultDepartmentId: string | null,
): EquipmentFormValues {
  const categoryId = item?.category_id ?? "";
  const category = categories.find((c) => c.id === categoryId);
  const num = (v: number | null | undefined) => (v == null ? "" : String(v));
  return {
    name: item?.name ?? "",
    category_id: categoryId,
    department_id: item ? (item.department_id ?? "") : (defaultDepartmentId ?? ""),
    quantity: String(item?.quantity ?? 1),
    serial_numbers: (item?.serial_numbers ?? []).join(", "),
    status: item?.status ?? "ok",
    insured_until: item?.insured_until ?? "",
    insurance_value: num(item?.insurance_value),
    purchase_price: num(item?.purchase_price),
    purchase_date: item?.purchase_date ?? "",
    rental_price_day: num(item?.rental_price_day),
    parts: (item?.parts ?? []).join("\n"),
    comment: item?.comment ?? "",
    bookable: item?.bookable ?? category?.bookable ?? true,
  };
}

/** «28 000 kr» → 28000. Tom → null. Ugyldig → NaN. */
export function parseKroner(v: string): number | null {
  const cleaned = v.replace(/kr/gi, "").replace(/\s/g, "").replace(",", ".");
  if (cleaned === "") return null;
  return Number(cleaned);
}

export type FormResult =
  | {
      ok: true;
      input: {
        name: string;
        category_id: string;
        department_id: string;
        quantity: number;
        serial_numbers: string[];
        status: EquipmentStatus;
        bookable: boolean | null;
        insured_until: string | null;
        insurance_value: number | null;
        purchase_price: number | null;
        purchase_date: string | null;
        rental_price_day: number | null;
        parts: string[];
        comment: string | null;
      };
    }
  | { ok: false; error: string };

/**
 * Validerer og gjør om til kolonneverdier. «Kan bookes» lagres bare når den
 * avviker fra kategorien (null = arv), så en senere endring på kategorien slår gjennom.
 */
export function toEquipmentInput(values: EquipmentFormValues, categories: DBEquipmentCategory[]): FormResult {
  const name = values.name.trim();
  if (!name) return { ok: false, error: "Navn er påkrevd" };
  const category = categories.find((c) => c.id === values.category_id);
  if (!category) return { ok: false, error: "Velg kategori" };
  if (!values.department_id) return { ok: false, error: "Velg sted" };
  const quantity = Number(values.quantity);
  if (!Number.isInteger(quantity) || quantity < 1) return { ok: false, error: "Antall må være et helt tall fra 1" };
  const amounts = {
    insurance_value: parseKroner(values.insurance_value),
    purchase_price: parseKroner(values.purchase_price),
    rental_price_day: parseKroner(values.rental_price_day),
  };
  if (Object.values(amounts).some((v) => v !== null && (!Number.isFinite(v) || v < 0))) {
    return { ok: false, error: "Beløp må være tall" };
  }
  const lines = (v: string, sep: RegExp) => v.split(sep).map((s) => s.trim()).filter(Boolean);
  return {
    ok: true,
    input: {
      name,
      category_id: category.id,
      department_id: values.department_id,
      quantity,
      serial_numbers: lines(values.serial_numbers, /[,\n]/),
      status: values.status,
      bookable: values.bookable === category.bookable ? null : values.bookable,
      insured_until: values.insured_until || null,
      ...amounts,
      purchase_date: values.purchase_date || null,
      parts: lines(values.parts, /\n/),
      comment: values.comment.trim() || null,
    },
  };
}
